import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
} from "@dnd-kit/core";
import {
  createContext,
  type MutableRefObject,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  moveInteraction,
  moveInteractions,
  reorderInteractions,
  reorderQuizFeedbackInteractions,
  reorderTakes,
  selectSelectedInteractionIds,
  selectTrainingType,
  setPrimaryInteraction,
  setSelectedInteraction,
  setSelectedTake,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import {
  findQuizById,
  findQuizFeedbackInteraction,
} from "../../app/slices/helpers/QuizFeedbackLocator";
import {
  getFeedbackArray,
  getFeedbackEligibleTypes,
} from "../../models/QuizFeedbackHelper";
import { IQuizFeedback, QuizFeedbackSlot } from "../../models/Quiz";
import Take from "../../models/Take";
import Interaction from "../../models/interactions/Interaction";

const TAKE_PREFIX = "take:";
const INTERACTION_PREFIX = "interaction:";
const INTERACTION_LIST_PREFIX = "interaction-list:";
const FEEDBACK_INTERACTION_PREFIX = "feedback-interaction:";
const FEEDBACK_LIST_PREFIX = "feedback-list:";

export const getTakeDndId = (takeId: string) => `${TAKE_PREFIX}${takeId}`;
export const getInteractionDndId = (interactionId: string) =>
  `${INTERACTION_PREFIX}${interactionId}`;
export const getInteractionListDndId = (takeId: string) =>
  `${INTERACTION_LIST_PREFIX}${takeId}`;
export const getFeedbackInteractionDndId = (interactionId: string) =>
  `${FEEDBACK_INTERACTION_PREFIX}${interactionId}`;
export const getFeedbackListDndId = (
  quizId: string,
  slot: QuizFeedbackSlot
) => `${FEEDBACK_LIST_PREFIX}${quizId}:${slot}`;

export type TakeInteractionDndData =
  | { kind: "take"; takeId: string }
  | { kind: "interaction"; takeId: string; interactionId: string }
  | { kind: "interaction-list"; takeId: string }
  | {
      kind: "feedback-interaction";
      takeId: string;
      quizId: string;
      slot: QuizFeedbackSlot;
      interactionId: string;
    }
  | {
      kind: "feedback-list";
      takeId: string;
      quizId: string;
      slot: QuizFeedbackSlot;
    };

export type InteractionDragSource =
  | { kind: "take" }
  | { kind: "quizFeedback"; quizId: string; slot: QuizFeedbackSlot };

export type ActiveTakeInteractionDrag<TTake extends Take = Take> =
  | { kind: "take"; take: TTake }
  | {
      kind: "interaction";
      takeId: string;
      source: InteractionDragSource;
      interaction: Interaction;
      // The dragged group in container order; contains only `interaction`
      // for a single-row drag.
      interactions: Interaction[];
    };

// Wraps a drag-overlay row with a count badge when a group is dragged.
export function GroupDragOverlayBadge(props: {
  count: number;
  children: ReactNode;
}) {
  const { count, children } = props;
  if (count <= 1) return <>{children}</>;
  return (
    <div className="relative">
      {children}
      <span
        className="absolute -top-2 -right-2 z-10 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-creator-primary px-1.5 text-[11px] font-semibold text-white shadow"
        data-testid="drag-overlay-count"
      >
        {count}
      </span>
    </div>
  );
}

interface TakeInteractionDndContextValue {
  hoverTakeId: string | null;
  // Set while a drag hovers a feedback slot it may not drop into (type
  // ineligible); sections render a blocked state from this.
  blockedDropTarget: { quizId: string; slot: QuizFeedbackSlot } | null;
  // Set while an eligible drag hovers a feedback slot other than its own;
  // sections render a drop-target highlight from this.
  hoverFeedbackSlot: { quizId: string; slot: QuizFeedbackSlot } | null;
  // True while any drag is in flight; the quiz feedback panel suspends its
  // selection-based auto-close so drop targets don't vanish mid-drag.
  isDragActive: boolean;
  // Synchronous mirror of isDragActive: drag start moves the selection, and
  // the resulting store-driven render can commit BEFORE the provider's own
  // state update, so effects must be able to read the in-flight flag without
  // waiting for a re-render.
  isDragActiveRef: MutableRefObject<boolean>;
}

const TakeInteractionDndContext =
  createContext<TakeInteractionDndContextValue>({
    hoverTakeId: null,
    blockedDropTarget: null,
    hoverFeedbackSlot: null,
    isDragActive: false,
    isDragActiveRef: { current: false },
  });

export function useTakeInteractionDndContext() {
  return useContext(TakeInteractionDndContext);
}

function findTakeByInteractionId<TTake extends Take>(
  takes: TTake[],
  interactionId: string
) {
  return takes.find((take) =>
    take.interactions.some((interaction) => interaction.id === interactionId)
  );
}

function resolveFeedbackInteractionData<TTake extends Take>(
  takes: TTake[],
  interactionId: string
): TakeInteractionDndData | null {
  const location = findQuizFeedbackInteraction(takes, interactionId);
  return location
    ? {
        kind: "feedback-interaction",
        takeId: location.takeId,
        quizId: location.quizId,
        slot: location.slot,
        interactionId,
      }
    : null;
}

function resolveDragData<TTake extends Take>(
  item: { id: string | number; data?: { current?: unknown } } | null,
  takes: TTake[]
): TakeInteractionDndData | null {
  if (!item) return null;

  const current = item.data?.current as Partial<TakeInteractionDndData> | undefined;
  if (current?.kind === "take" && current.takeId) {
    return { kind: "take", takeId: current.takeId };
  }
  if (
    current?.kind === "interaction" &&
    current.takeId &&
    current.interactionId
  ) {
    return {
      kind: "interaction",
      takeId: current.takeId,
      interactionId: current.interactionId,
    };
  }
  if (current?.kind === "interaction-list" && current.takeId) {
    return { kind: "interaction-list", takeId: current.takeId };
  }
  if (
    current?.kind === "feedback-interaction" &&
    current.takeId &&
    current.quizId &&
    current.slot &&
    current.interactionId
  ) {
    return {
      kind: "feedback-interaction",
      takeId: current.takeId,
      quizId: current.quizId,
      slot: current.slot,
      interactionId: current.interactionId,
    };
  }
  if (
    current?.kind === "feedback-list" &&
    current.takeId &&
    current.quizId &&
    current.slot
  ) {
    return {
      kind: "feedback-list",
      takeId: current.takeId,
      quizId: current.quizId,
      slot: current.slot,
    };
  }

  const id = item.id.toString();
  if (id.startsWith(INTERACTION_LIST_PREFIX)) {
    return {
      kind: "interaction-list",
      takeId: id.slice(INTERACTION_LIST_PREFIX.length),
    };
  }
  if (id.startsWith(FEEDBACK_INTERACTION_PREFIX)) {
    return resolveFeedbackInteractionData(
      takes,
      id.slice(FEEDBACK_INTERACTION_PREFIX.length)
    );
  }
  if (id.startsWith(FEEDBACK_LIST_PREFIX)) {
    const [quizId, slot] = id.slice(FEEDBACK_LIST_PREFIX.length).split(":");
    const quizLocation =
      quizId && (slot === "correct" || slot === "wrong")
        ? findQuizById(takes, quizId)
        : undefined;
    return quizLocation
      ? {
          kind: "feedback-list",
          takeId: takes[quizLocation.takeIndex].id,
          quizId,
          slot: slot as QuizFeedbackSlot,
        }
      : null;
  }
  if (id.startsWith(TAKE_PREFIX)) {
    return { kind: "take", takeId: id.slice(TAKE_PREFIX.length) };
  }
  if (id.startsWith(INTERACTION_PREFIX)) {
    const interactionId = id.slice(INTERACTION_PREFIX.length);
    const take = findTakeByInteractionId(takes, interactionId);
    return take
      ? { kind: "interaction", takeId: take.id, interactionId }
      : null;
  }

  const take = takes.find((candidate) => candidate.id === id);
  if (take) return { kind: "take", takeId: take.id };

  const interactionTake = findTakeByInteractionId(takes, id);
  if (interactionTake) {
    return {
      kind: "interaction",
      takeId: interactionTake.id,
      interactionId: id,
    };
  }
  return resolveFeedbackInteractionData(takes, id);
}

function getDestinationTakeId(data: TakeInteractionDndData | null) {
  return data?.takeId ?? null;
}

export default function TakeInteractionDndProvider<TTake extends Take>(props: {
  takes: TTake[];
  children: ReactNode;
  renderOverlay: (
    activeDrag: ActiveTakeInteractionDrag<TTake> | null
  ) => ReactNode;
}) {
  const { takes, children, renderOverlay } = props;
  const dispatch = useDispatch();
  const selectedInteractionIds = useSelector(selectSelectedInteractionIds);
  const trainingType = useSelector(selectTrainingType);
  const [activeDrag, setActiveDrag] =
    useState<ActiveTakeInteractionDrag<TTake> | null>(null);
  const [hoverTakeId, setHoverTakeId] = useState<string | null>(null);
  const [blockedDropTarget, setBlockedDropTarget] = useState<{
    quizId: string;
    slot: QuizFeedbackSlot;
  } | null>(null);
  const [hoverFeedbackSlot, setHoverFeedbackSlot] = useState<{
    quizId: string;
    slot: QuizFeedbackSlot;
  } | null>(null);
  const isDragActiveRef = useRef(false);
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 10 },
  });

  const feedbackEligibleTypes = getFeedbackEligibleTypes(
    trainingType === TrainingType.Training3D
  );

  const resetDragState = () => {
    isDragActiveRef.current = false;
    setActiveDrag(null);
    setHoverTakeId(null);
    setBlockedDropTarget(null);
    setHoverFeedbackSlot(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = resolveDragData(event.active, takes);
    if (!data || data.kind === "interaction-list" || data.kind === "feedback-list")
      return;

    if (data.kind === "take") {
      const take = takes.find((candidate) => candidate.id === data.takeId);
      if (!take) return;
      dispatch(setSelectedTake(take.id));
      setActiveDrag({ kind: "take", take });
      return;
    }

    let containerInteractions: Interaction[] | undefined;
    let source: InteractionDragSource;
    if (data.kind === "feedback-interaction") {
      const quizLocation = findQuizById(takes, data.quizId);
      containerInteractions = quizLocation
        ? getFeedbackArray(quizLocation.quiz, data.slot)
        : undefined;
      source = { kind: "quizFeedback", quizId: data.quizId, slot: data.slot };
    } else {
      containerInteractions = takes.find(
        (candidate) => candidate.id === data.takeId
      )?.interactions;
      source = { kind: "take" };
    }
    const interaction = containerInteractions?.find(
      (candidate) => candidate.id === data.interactionId
    );
    if (!containerInteractions || !interaction) return;

    const isGroupDrag =
      selectedInteractionIds.length > 1 &&
      selectedInteractionIds.includes(interaction.id);
    const interactions = isGroupDrag
      ? containerInteractions.filter((candidate) =>
          selectedInteractionIds.includes(candidate.id)
        )
      : [interaction];

    // Mark the drag active BEFORE moving the selection: the quiz feedback
    // panel's auto-close effect runs on the selection change and must already
    // see the drag in flight.
    isDragActiveRef.current = true;
    setActiveDrag({
      kind: "interaction",
      takeId: data.takeId,
      source,
      interaction,
      interactions,
    });
    // Dragging a selected row keeps the group and only refocuses the primary;
    // dragging an unselected row replaces the selection.
    dispatch(
      isGroupDrag
        ? setPrimaryInteraction(interaction.id)
        : setSelectedInteraction(interaction.id)
    );
  };

  const isFeedbackTarget = (data: TakeInteractionDndData | null) =>
    data?.kind === "feedback-interaction" || data?.kind === "feedback-list";

  const handleDragOver = (event: DragOverEvent) => {
    const activeData = resolveDragData(event.active, takes);
    const overData = resolveDragData(event.over, takes);
    if (
      !activeData ||
      !overData ||
      activeData.kind === "interaction-list" ||
      activeData.kind === "feedback-list"
    ) {
      setHoverTakeId(null);
      setBlockedDropTarget(null);
      setHoverFeedbackSlot(null);
      return;
    }

    if (isFeedbackTarget(overData)) {
      setHoverTakeId(null);
      const target = overData as Extract<
        TakeInteractionDndData,
        { kind: "feedback-interaction" | "feedback-list" }
      >;
      const dragged =
        activeDrag?.kind === "interaction" ? activeDrag.interactions : null;
      const blocked =
        activeData.kind === "take" ||
        (dragged !== null &&
          dragged.some(
            (candidate) => !feedbackEligibleTypes.has(candidate.type)
          ));
      setBlockedDropTarget(
        blocked ? { quizId: target.quizId, slot: target.slot } : null
      );
      // Highlight the hovered slot as a drop target, unless it is the slot
      // the drag started in (plain reorder needs no highlight).
      const sourceIsSameSlot =
        activeData.kind === "feedback-interaction" &&
        activeData.quizId === target.quizId &&
        activeData.slot === target.slot;
      setHoverFeedbackSlot(
        !blocked && dragged !== null && !sourceIsSameSlot
          ? { quizId: target.quizId, slot: target.slot }
          : null
      );
      return;
    }

    setBlockedDropTarget(null);
    setHoverFeedbackSlot(null);
    const destinationTakeId = getDestinationTakeId(overData);
    // A drag out of a feedback slot always crosses containers, so the
    // destination Take highlights even when it is the slot's parent Take.
    const crossesContainer =
      activeData.kind === "feedback-interaction" ||
      activeData.takeId !== destinationTakeId;
    setHoverTakeId(
      destinationTakeId && crossesContainer ? destinationTakeId : null
    );
  };

  // Group drop rule: dropping on a Take card or list appends the group;
  // dropping on a row inserts after it, except when dragging upward within
  // the same Take, where the group lands before the hovered row. A drop on a
  // group member is a no-op.
  const handleGroupDragEnd = (
    groupInteractions: Interaction[],
    destinationTake: TTake,
    overData: TakeInteractionDndData
  ) => {
    const groupIds = groupInteractions.map((interaction) => interaction.id);
    const destinationIds = destinationTake.interactions.map(
      (interaction) => interaction.id
    );

    let afterInteractionId: string | undefined;
    if (overData.kind === "interaction") {
      if (groupIds.includes(overData.interactionId)) return;
      const hoveredIndex = destinationIds.indexOf(overData.interactionId);
      if (hoveredIndex === -1) return;

      const firstGroupIndex = destinationIds.findIndex((id) =>
        groupIds.includes(id)
      );
      const isDraggingUp =
        firstGroupIndex !== -1 && hoveredIndex < firstGroupIndex;
      afterInteractionId = isDraggingUp
        ? destinationIds
            .slice(0, hoveredIndex)
            .reverse()
            .find((id) => !groupIds.includes(id))
        : overData.interactionId;
    } else {
      afterInteractionId = [...destinationIds]
        .reverse()
        .find((id) => !groupIds.includes(id));
    }

    dispatch(
      moveInteractions({
        interactionIds: groupIds,
        destination: {
          kind: "take",
          takeId: destinationTake.id,
          afterInteractionId,
        },
      })
    );
  };

  // Resolves the dragged interactions + their source container, falling back
  // to the drag data when the overlay state is missing (e.g. in tests).
  const resolveDraggedGroup = (
    activeData: TakeInteractionDndData
  ): { interactions: Interaction[]; source: InteractionDragSource } | null => {
    if (
      activeDrag?.kind === "interaction" &&
      (activeData.kind === "interaction" ||
        activeData.kind === "feedback-interaction") &&
      activeDrag.interaction.id === activeData.interactionId
    ) {
      return {
        interactions: activeDrag.interactions,
        source: activeDrag.source,
      };
    }
    if (activeData.kind === "interaction") {
      const take = takes.find(
        (candidate) => candidate.id === activeData.takeId
      );
      const interaction = take?.interactions.find(
        (candidate) => candidate.id === activeData.interactionId
      );
      return interaction
        ? { interactions: [interaction], source: { kind: "take" } }
        : null;
    }
    if (activeData.kind === "feedback-interaction") {
      const quizLocation = findQuizById(takes, activeData.quizId);
      const interaction = quizLocation
        ? getFeedbackArray(quizLocation.quiz, activeData.slot).find(
            (candidate) => candidate.id === activeData.interactionId
          )
        : undefined;
      return interaction
        ? {
            interactions: [interaction],
            source: {
              kind: "quizFeedback",
              quizId: activeData.quizId,
              slot: activeData.slot,
            },
          }
        : null;
    }
    return null;
  };

  const handleFeedbackDropEnd = (
    dragInteractions: Interaction[],
    source: InteractionDragSource,
    overData: Extract<
      TakeInteractionDndData,
      { kind: "feedback-interaction" | "feedback-list" }
    >
  ) => {
    const dragIds = dragInteractions.map((interaction) => interaction.id);

    if (
      dragInteractions.some(
        (interaction) => !feedbackEligibleTypes.has(interaction.type)
      )
    ) {
      toast.error(
        "The selection contains interactions that can't be placed in quiz feedback",
        { position: "top-right" }
      );
      return;
    }
    // A quiz can never be dropped into its own feedback.
    if (dragIds.includes(overData.quizId)) return;

    const quizLocation = findQuizById(takes, overData.quizId);
    if (!quizLocation) return;
    const slotIds = getFeedbackArray(
      quizLocation.quiz as IQuizFeedback,
      overData.slot
    ).map((interaction) => interaction.id);

    const sameSlot =
      source.kind === "quizFeedback" &&
      source.quizId === overData.quizId &&
      source.slot === overData.slot;

    if (sameSlot && dragIds.length === 1) {
      // Single-row reorder within a slot keeps the pre-existing semantics
      // (dragging up lands before the hovered row).
      if (overData.kind !== "feedback-interaction") return;
      const startIndex = slotIds.indexOf(dragIds[0]);
      const endIndex = slotIds.indexOf(overData.interactionId);
      if (startIndex !== -1 && endIndex !== -1 && startIndex !== endIndex) {
        dispatch(
          reorderQuizFeedbackInteractions({
            quizId: overData.quizId,
            slot: overData.slot,
            startIndex,
            endIndex,
          })
        );
      }
      return;
    }

    const hoveredId =
      overData.kind === "feedback-interaction" ? overData.interactionId : null;
    if (hoveredId && dragIds.includes(hoveredId)) return;

    let afterInteractionId: string | undefined;
    let atStart = false;
    if (hoveredId) {
      const hoveredIndex = slotIds.indexOf(hoveredId);
      if (hoveredIndex === -1) return;
      const firstGroupIndex = slotIds.findIndex((id) => dragIds.includes(id));
      const isDraggingUp =
        firstGroupIndex !== -1 && hoveredIndex < firstGroupIndex;
      afterInteractionId = isDraggingUp
        ? slotIds
            .slice(0, hoveredIndex)
            .reverse()
            .find((id) => !dragIds.includes(id))
        : hoveredId;
      if (isDraggingUp && !afterInteractionId) atStart = true;
    }

    const destination = {
      kind: "quizFeedback" as const,
      quizId: overData.quizId,
      slot: overData.slot,
      afterInteractionId,
      ...(atStart && { atStart: true }),
    };
    dispatch(
      dragIds.length > 1
        ? moveInteractions({ interactionIds: dragIds, destination })
        : moveInteraction({ interactionId: dragIds[0], destination })
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeData = resolveDragData(event.active, takes);
    const overData = resolveDragData(event.over, takes);

    if (
      !activeData ||
      !overData ||
      activeData.kind === "interaction-list" ||
      activeData.kind === "feedback-list"
    ) {
      resetDragState();
      return;
    }

    if (activeData.kind === "take") {
      if (overData.kind === "take") {
        const startIndex = takes.findIndex(
          (take) => take.id === activeData.takeId
        );
        const endIndex = takes.findIndex((take) => take.id === overData.takeId);
        if (startIndex !== -1 && endIndex !== -1 && startIndex !== endIndex) {
          dispatch(reorderTakes({ startIndex, endIndex }));
        }
      }
      resetDragState();
      return;
    }

    const dragged = resolveDraggedGroup(activeData);
    if (!dragged) {
      resetDragState();
      return;
    }

    if (isFeedbackTarget(overData)) {
      handleFeedbackDropEnd(
        dragged.interactions,
        dragged.source,
        overData as Extract<
          TakeInteractionDndData,
          { kind: "feedback-interaction" | "feedback-list" }
        >
      );
      resetDragState();
      return;
    }

    const destinationTake = takes.find(
      (take) => take.id === getDestinationTakeId(overData)
    );
    if (!destinationTake) {
      resetDragState();
      return;
    }

    if (dragged.source.kind === "quizFeedback") {
      // Out of a feedback slot into a Take: over a row inserts after it,
      // over a card/list appends.
      const dragIds = dragged.interactions.map(
        (interaction) => interaction.id
      );
      const afterInteractionId =
        overData.kind === "interaction"
          ? overData.interactionId
          : destinationTake.interactions[
              destinationTake.interactions.length - 1
            ]?.id;
      const destination = {
        kind: "take" as const,
        takeId: destinationTake.id,
        afterInteractionId,
      };
      dispatch(
        dragIds.length > 1
          ? moveInteractions({ interactionIds: dragIds, destination })
          : moveInteraction({ interactionId: dragIds[0], destination })
      );
      resetDragState();
      return;
    }

    const sourceTake = takes.find((take) => take.id === activeData.takeId);
    if (!sourceTake) {
      resetDragState();
      return;
    }

    if (dragged.interactions.length > 1) {
      handleGroupDragEnd(dragged.interactions, destinationTake, overData);
      resetDragState();
      return;
    }

    const startIndex = sourceTake.interactions.findIndex(
      (interaction) => interaction.id === dragged.interactions[0].id
    );
    if (startIndex === -1) {
      resetDragState();
      return;
    }

    let endIndex: number;
    if (sourceTake.id !== destinationTake.id) {
      endIndex = destinationTake.interactions.length;
    } else if (overData.kind === "interaction") {
      endIndex = destinationTake.interactions.findIndex(
        (interaction) => interaction.id === overData.interactionId
      );
      if (endIndex === -1) {
        resetDragState();
        return;
      }
    } else {
      resetDragState();
      return;
    }

    if (
      sourceTake.id !== destinationTake.id ||
      startIndex !== endIndex
    ) {
      dispatch(
        reorderInteractions({
          startTakeId: sourceTake.id,
          destinationTakeId: destinationTake.id,
          startIndex,
          endIndex,
        })
      );
    }

    resetDragState();
  };

  const contextValue = useMemo(
    () => ({
      hoverTakeId,
      blockedDropTarget,
      hoverFeedbackSlot,
      isDragActive: activeDrag !== null,
      isDragActiveRef,
    }),
    [hoverTakeId, blockedDropTarget, hoverFeedbackSlot, activeDrag]
  );

  const handleDragCancel = (_event: DragCancelEvent) => resetDragState();

  return (
    <TakeInteractionDndContext.Provider value={contextValue}>
      <DndContext
        sensors={[mouseSensor]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay>{renderOverlay(activeDrag)}</DragOverlay>
      </DndContext>
    </TakeInteractionDndContext.Provider>
  );
}
